import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Container, Stack, Text, Title, TextInput, PasswordInput, Button, Paper, Anchor, PinInput } from '@mantine/core'
import { IconMail, IconLock, IconEdit, IconShieldCheck } from '@tabler/icons-react'
import { useAuth } from '../hooks/useAuth'

const inputStyles = {
  input: {
    background: 'rgba(10, 22, 40, 0.8)',
    border: '1px solid rgba(0, 255, 255, 0.15)',
    color: '#e0e8f0',
  },
  label: { color: 'var(--mantine-color-gray-4)' },
}

export default function EmailCapture() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { requestVerificationCode, verifyCode, login } = useAuth()
  const returnTo = searchParams.get('returnTo')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [mode, setMode] = useState('email') // 'email' | 'verify' | 'password'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1)
    }, 1000)
    return () => clearInterval(timer)
  }, [resendCooldown])

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim()) return

    setLoading(true)
    setError('')

    const result = await requestVerificationCode(email.trim())
    setLoading(false)

    if (result.success) {
      if (result.status === 'full_account') {
        setMode('password')
      } else {
        setMode('verify')
        setResendCooldown(30)
      }
    } else {
      setError(result.error)
    }
  }

  const handleVerifySubmit = async (e) => {
    e?.preventDefault()
    if (code.length !== 6) return

    setLoading(true)
    setError('')

    const result = await verifyCode(email.trim(), code, returnTo || `/play/${id}`)
    setLoading(false)

    if (!result.success) {
      setError(result.error)
    }
  }

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0) return

    setError('')
    const result = await requestVerificationCode(email.trim())
    if (result.success) {
      setResendCooldown(30)
      setCode('')
    } else {
      setError(result.error)
    }
  }, [email, resendCooldown, requestVerificationCode])

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    if (!password) return

    setLoading(true)
    setError('')

    const result = await login(email.trim(), password, returnTo || `/play/${id}`)
    setLoading(false)

    if (!result.success) {
      setError(result.error)
    }
  }

  const handleBackToEmail = () => {
    setMode('email')
    setPassword('')
    setCode('')
    setError('')
  }

  const titles = {
    email: 'Quick Start',
    verify: 'Check Your Email',
    password: 'Welcome Back',
  }

  const descriptions = {
    email: 'Enter your email to jump right in. You can create a full account later to save your progress.',
    verify: `We sent a 6-digit code to ${email}. Enter it below to continue.`,
    password: 'This email has a full account. Enter your password to continue.',
  }

  return (
    <Container size="xs" py="xl">
      <Stack align="center" gap="lg" style={{ minHeight: '50vh', justifyContent: 'center' }}>
        <Title order={2} ff="Orbitron, sans-serif" c="cyan.4" ta="center">
          {titles[mode]}
        </Title>
        <Text c="dimmed" ta="center" maw={360}>
          {descriptions[mode]}
        </Text>

        <Paper p="xl" className="glass" w="100%">
          {mode === 'email' && (
            <form onSubmit={handleEmailSubmit}>
              <Stack gap="md">
                <TextInput
                  label="Email"
                  placeholder="your@email.com"
                  leftSection={<IconMail size={16} />}
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  type="email"
                  required
                  error={error}
                  styles={inputStyles}
                />

                <Button type="submit" loading={loading} fullWidth size="md">
                  Start Playing
                </Button>
              </Stack>
            </form>
          )}

          {mode === 'verify' && (
            <form onSubmit={handleVerifySubmit}>
              <Stack gap="md" align="center">
                <TextInput
                  label="Email"
                  value={email}
                  readOnly
                  leftSection={<IconMail size={16} />}
                  rightSection={
                    <IconEdit
                      size={16}
                      style={{ cursor: 'pointer', color: 'var(--mantine-color-cyan-4)' }}
                      onClick={handleBackToEmail}
                    />
                  }
                  styles={inputStyles}
                  w="100%"
                />

                <PinInput
                  length={6}
                  type="number"
                  value={code}
                  onChange={setCode}
                  size="lg"
                  styles={{
                    input: {
                      background: 'rgba(10, 22, 40, 0.8)',
                      border: '1px solid rgba(0, 255, 255, 0.15)',
                      color: '#e0e8f0',
                      fontFamily: 'Orbitron, sans-serif',
                    },
                  }}
                />

                {error && (
                  <Text c="red" size="sm" ta="center">
                    {error}
                  </Text>
                )}

                <Button
                  type="submit"
                  loading={loading}
                  fullWidth
                  size="md"
                  leftSection={<IconShieldCheck size={18} />}
                  disabled={code.length !== 6}
                >
                  Verify & Play
                </Button>

                <Text size="sm" c="dimmed">
                  {resendCooldown > 0 ? (
                    `Resend code in ${resendCooldown}s`
                  ) : (
                    <Anchor c="cyan.4" onClick={handleResend}>
                      Resend code
                    </Anchor>
                  )}
                </Text>
              </Stack>
            </form>
          )}

          {mode === 'password' && (
            <form onSubmit={handlePasswordSubmit}>
              <Stack gap="md">
                <TextInput
                  label="Email"
                  value={email}
                  readOnly
                  leftSection={<IconMail size={16} />}
                  rightSection={
                    <IconEdit
                      size={16}
                      style={{ cursor: 'pointer', color: 'var(--mantine-color-cyan-4)' }}
                      onClick={handleBackToEmail}
                    />
                  }
                  styles={inputStyles}
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  leftSection={<IconLock size={16} />}
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  required
                  error={error}
                  styles={inputStyles}
                />

                <Anchor
                  c="cyan.4"
                  size="sm"
                  onClick={() => navigate('/forgot-password')}
                  style={{ alignSelf: 'flex-end', marginTop: -8 }}
                >
                  Forgot password?
                </Anchor>

                <Button type="submit" loading={loading} fullWidth size="md">
                  Log In & Play
                </Button>
              </Stack>
            </form>
          )}
        </Paper>

        <Text size="sm" c="dimmed">
          Already have an account?{' '}
          <Anchor c="cyan.4" onClick={() => navigate('/login')}>
            Log in
          </Anchor>
        </Text>
      </Stack>
    </Container>
  )
}
